/** /tags/canonicals — cross-tenant Tag_Canonical catalog (grid + create/edit
 *  dialog). Aliases say which spellings are the same thing; a canonical says
 *  what is TRUE about it. A tag's effective attributes are its own data over
 *  its canonical's, so a policy written once here covers every spelling —
 *  including one ingestion mints next week. A null tenant is system-wide.
 *  Filters (operator scope / category / enabled) and search are held in local
 *  state and passed to the list query as `extra`. */
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import { Box, Button, MenuItem, TextField } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid-pro';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';

import * as canonicalsApi from '@/api/tagCanonicals';
import type { TagCanonical } from '@/api/tagCanonicals';
import { deleteBatchAction } from '@/components/DataGrid/batchTypes';
import { copyableColumn } from '@/components/DataGrid/copyableColumn';
import {
  ResourceDataGrid,
  type ResourceDataGridParams,
} from '@/components/DataGrid/ResourceDataGrid';
import { ResponsiveFilterBar, type FilterBarItem } from '@/components/DataGrid/ResponsiveFilterBar';
import { PageHeader } from '@/components/PageHeader';
import { TenantLinkCell } from '@/components/TenantMark/TenantMark';
import {
  pickedSentinel,
  TenantPicker,
  type TenantPickerValue,
} from '@/components/TenantPicker/TenantPicker';
import { useCreateIntent } from '@/hooks/useCreateIntent';
import { usePermissions } from '@/hooks/usePermissions';
import { PAGE_FILL_SX } from '@/layout/pageFill';
import { formatRelativeTime } from '@/utils/format';
import { pageTenantsFrom } from '@/scope/pageTenants';
import { formatTenantIds } from '@/scope/tenantIds';
import { useScopedTenantsState } from '@/scope/useScopedTenantsState';

import { TagCanonicalFormDialog } from './TagCanonicalFormDialog';

/** Sentinel picker option: system-wide canonicals (`tour_operator IS NULL`). */
const SYSTEM_TENANT: TenantPickerValue = { id: 0, name: 'System / global (no operator)' };
const SYSTEM_EXTRA_OPTIONS = [SYSTEM_TENANT];

export function TagCanonicalsListPage() {
  const { hasPerm } = usePermissions();
  const canManage = hasPerm('console_tag_aliases_manage');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);

  // The operators (several, D1) or the System sentinel alone (D3); they follow
  // the console scope until picked here.
  const [tenants, setTenants] = useScopedTenantsState();
  const [categoryFilter, setCategoryFilter] = useState('');
  const [enabledFilter, setEnabledFilter] = useState<'any' | 'true' | 'false'>('any');

  const tenantIds = formatTenantIds(tenants.map((t) => t.id));
  const tenantParam = pickedSentinel(tenants, SYSTEM_EXTRA_OPTIONS) ? 'system' : tenantIds;

  const anyFilterActive = tenants.length > 0 || categoryFilter !== '' || enabledFilter !== 'any';
  const resetAllFilters = () => {
    setTenants([]);
    setCategoryFilter('');
    setEnabledFilter('any');
  };

  const extra = useMemo(() => {
    const e: Record<string, string | number | boolean> = {};
    if (tenantParam) e.tour_operator_id = tenantParam;
    if (categoryFilter.trim()) e.category_code = categoryFilter.trim().toLowerCase();
    if (enabledFilter !== 'any') e.enabled = enabledFilter === 'true';
    return e;
  }, [tenantParam, categoryFilter, enabledFilter]);

  const editQuery = useQuery({
    queryKey: ['tag_canonicals', 'detail', editId],
    queryFn: () => canonicalsApi.retrieve(editId as number),
    enabled: editId !== null,
  });

  // The create dialog features the page's operators and pre-selects the one when
  // there is exactly one (D9); the System sentinel (id 0) names no operator.
  const pageTenants = pageTenantsFrom(tenantIds, tenants);

  const openCreate = () => {
    setEditId(null);
    setDialogOpen(true);
  };
  const openEdit = useCallback((id: number) => {
    setEditId(id);
    setDialogOpen(true);
  }, []);

  useCreateIntent(() => openCreate());

  const columns = useMemo<GridColDef<TagCanonical>[]>(
    () => [
      copyableColumn({ field: 'id', headerName: 'ID', width: 80 }),
      {
        field: 'category_code',
        headerName: 'Category',
        width: 150,
      },
      {
        field: 'token',
        headerName: 'Canonical',
        flex: 1,
        minWidth: 200,
        // The `~` is the spelling conditions use (reseller:~viator), so the
        // grid reads the same way the chips and template tags do.
        renderCell: (p) =>
          canManage ? (
            <Box
              component="button"
              type="button"
              onClick={() => openEdit(p.row.id)}
              sx={{
                background: 'none',
                border: 'none',
                p: 0,
                cursor: 'pointer',
                color: '#0a1a3f',
                fontWeight: 500,
                font: 'inherit',
                textAlign: 'left',
              }}
            >
              <code>~{p.row.token}</code>
            </Box>
          ) : (
            <code>~{p.row.token}</code>
          ),
      },
      {
        // Scope-local by design: a system canonical does not count tenants'
        // private remaps. Not in the backend's ordering whitelist, so a header
        // sort would silently no-op — display-only.
        field: 'alias_count',
        headerName: 'Aliases',
        width: 100,
        align: 'right',
        headerAlign: 'right',
        sortable: false,
        description: 'Names that normalise onto this token, within the same scope.',
      },
      {
        field: 'tenant',
        headerName: 'Operator',
        width: 170,
        valueGetter: (_, row) => row.tenant?.name ?? 'System',
        renderCell: (p) => <TenantLinkCell tenant={p.row.tenant} empty="System" />,
      },
      {
        // Filter-only on the backend — not in _ORDER_FIELDS, so a header sort
        // would silently fall back to category_code. Display-only.
        field: 'enabled',
        headerName: 'Enabled',
        width: 100,
        align: 'center',
        headerAlign: 'center',
        sortable: false,
        renderCell: (p) =>
          p.value ? <CheckIcon fontSize="small" color="success" aria-label="yes" /> : null,
      },
      {
        field: 'updated_ts',
        headerName: 'Updated',
        width: 140,
        sortable: true,
        renderCell: (p) => (
          <span title={p.row.updated_ts ?? ''}>{formatRelativeTime(p.row.updated_ts)}</span>
        ),
      },
    ],
    [canManage, openEdit],
  );

  const queryFn = useCallback(
    (params: ResourceDataGridParams) =>
      canonicalsApi.list({
        page: params.page,
        pageSize: params.pageSize,
        ordering: params.ordering ?? 'category_code',
        search: params.search,
        ...(params.extra as Record<string, never>),
      }),
    [],
  );

  const toolbarItems: FilterBarItem[] = [
    {
      key: 'scope',
      priority: 5,
      active: tenants.length > 0,
      node: (
        <Box sx={{ minWidth: 280 }}>
          <TenantPicker
            multiple
            value={tenants}
            onChange={setTenants}
            label="Operator"
            extraOptions={SYSTEM_EXTRA_OPTIONS}
          />
        </Box>
      ),
    },
    {
      key: 'category',
      priority: 4,
      active: categoryFilter !== '',
      node: (
        <TextField
          size="small"
          label="Category"
          value={categoryFilter}
          sx={{ width: 180 }}
          placeholder="reseller"
          onChange={(e) => setCategoryFilter(e.target.value)}
        />
      ),
    },
    {
      key: 'enabled',
      priority: 3,
      active: enabledFilter !== 'any',
      node: (
        <TextField
          select
          size="small"
          label="Enabled"
          value={enabledFilter}
          sx={{ width: 160 }}
          onChange={(e) => setEnabledFilter(e.target.value as 'any' | 'true' | 'false')}
        >
          <MenuItem value="any">Any</MenuItem>
          <MenuItem value="true">Enabled</MenuItem>
          <MenuItem value="false">Disabled</MenuItem>
        </TextField>
      ),
    },
  ];

  return (
    <Box sx={PAGE_FILL_SX}>
      <PageHeader
        helpStory="tag-canonicals"
        title="Canonicals"
        subtitle="Attributes that belong to the reseller rather than to any one spelling of its name. A tag's own data wins key by key; everything else is inherited from here."
        actions={
          canManage && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
              New canonical
            </Button>
          )
        }
      />

      <ResponsiveFilterBar items={toolbarItems} />

      <ResourceDataGrid<TagCanonical>
        queryKey={['tag_canonicals']}
        queryFn={queryFn}
        columns={columns}
        extra={extra}
        batchResource="tag_canonicals"
        batchActions={[deleteBatchAction('console_tag_aliases_manage')]}
        defaultSort={{ field: 'category_code', sort: 'asc' }}
        emptyMessage="No canonicals yet."
        headerToolbar={{
          onResetAllFilters: resetAllFilters,
          resetDisabled: !anyFilterActive,
          search: true,
          searchPlaceholder: 'Token, category…',
          exportFileName: 'tag-canonicals',
        }}
      />

      {dialogOpen && (editId === null || editQuery.data) && (
        <TagCanonicalFormDialog
          open={dialogOpen}
          canonical={editId === null ? null : (editQuery.data ?? null)}
          pageTenants={pageTenants}
          defaultCategory={categoryFilter.trim().toLowerCase()}
          onClose={() => {
            setDialogOpen(false);
            setEditId(null);
          }}
        />
      )}
    </Box>
  );
}
